import React, { useState } from 'react'
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useAdminOrganizer, useAdminOrganizers } from '@hooks'

interface OrganizerPickerProps {
  /** Selected organizer account id, "" = any. */
  value: string
  onChange: (accountId: string) => void
  testID: string
}

/** Drop-down with search: filters the list of events on one organizer. */
export const OrganizerPicker: React.FC<OrganizerPickerProps> = ({ value, onChange, testID }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selected = useAdminOrganizer(value || undefined)
  const list = useAdminOrganizers(search.trim(), 'active')
  const label = selected.data
    ? [`${selected.data.firstName} ${selected.data.lastName}`.trim() || selected.data.email, selected.data.organizationName]
        .filter(Boolean)
        .join(' · ')
    : t('admin.events.anyOrganizer')

  return (
    <View style={styles.wrapper}>
      <Text type="bodySmallBold" color={theme.colors.secondary}>
        {t('admin.events.organizerFilter')}
      </Text>
      <View style={styles.field}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={`${t('admin.events.organizerFilter')}: ${label}`}
          testID={testID}
          style={styles.pressable}
          onPress={() => setOpen((current) => !current)}
        >
          <Text type="bodySmall" color={value ? theme.colors.primary : theme.colors.greyBlue} numberOfLines={1} style={styles.flex}>
            {label}
          </Text>
          <Icon name={open ? 'arrowUp' : 'arrowDown'} size={14} color={theme.colors.greyBlue} />
        </TouchableOpacity>
        {value ? (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={t('admin.events.clearOrganizer')}
            testID={`${testID}-clear`}
            style={styles.clear}
            onPress={() => {
              onChange('')
              setOpen(false)
            }}
          >
            <Icon name="cross" size={theme.iconSize.small} color={theme.colors.greyBlue} />
          </TouchableOpacity>
        ) : null}
      </View>
      {open ? (
        <View style={styles.menu} testID={`${testID}-menu`}>
          <TextInput
            testID={`${testID}-search`}
            iconLeft="search"
            placeholder={t('admin.organizers.searchPlaceholder')}
            value={search}
            onChangeText={setSearch}
            autoCapitalize="none"
          />
          <ScrollView style={styles.options} nestedScrollEnabled>
            {(list.data?.data ?? []).length === 0 ? (
              <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.empty}>
                {t('admin.organizers.empty')}
              </Text>
            ) : (
              list.data?.data.map((organizer) => (
                <TouchableOpacity
                  key={organizer.accountId}
                  accessibilityRole="button"
                  testID={`${testID}-option-${organizer.accountId}`}
                  style={styles.option}
                  onPress={() => {
                    onChange(organizer.accountId)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  <Text type="bodySmallBold" numberOfLines={1}>
                    {`${organizer.firstName} ${organizer.lastName}`.trim() || organizer.email}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={1}>
                    {[organizer.organizationName, organizer.email].filter(Boolean).join(' · ')}
                  </Text>
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </View>
      ) : null}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    wrapper: { gap: 6, flex: 1, minWidth: 220 },
    flex: { flex: 1 },
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: theme.border.jumbo * 2,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      paddingHorizontal: theme.spacing.md,
    },
    pressable: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingVertical: theme.spacing.md,
    },
    clear: { padding: theme.spacing.sm },
    menu: {
      gap: theme.spacing.sm,
      padding: theme.spacing.sm,
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
    },
    options: { maxHeight: 220 },
    option: { paddingVertical: theme.spacing.sm, paddingHorizontal: theme.spacing.sm, gap: 2 },
    empty: { padding: theme.spacing.md },
  })
